// @workflow tasks.show-in-3d
/**
 * The task board viewer panel's per-tab steps, moved out of Task/views/index.vue: show the
 * isolated tasks' tags in 3D, on the 2D plan, or in 360. Each takes
 *   ctx = { store, surface, viewer, timers? }
 *     surface  the board's panel (taskBoardSurface.js contract)
 *     viewer   the viewer seam (taskViewerSeam.js), the only way to the Forge viewer
 *     timers   { setTimeout }, injectable for tests
 * and the tags to show (full tags from GET_BASE_TAGS_BY_IDS).
 */
import { ForgeAvaInspect, ForgeScaffold, Image360 } from '@/scripts/js/forgeModels';
import { getBoxAroundPoints } from '@/utils/threejsUtils';
import images360Service from '@Dashboard/service/images360.service';
import { log, error as debugError } from '@/utils/debugLogger';
import { TASK_VIEWER_TAB } from './taskContract';
import { isPanoramaPlan, taskSpatialPlan } from './taskViewerPlan';

/** What the user reads when a tab can't show the tasks (the board's words, unchanged). */
export const TASK_VIEWER_TEXT = Object.freeze({
  NO_POSITION: 'No position data available for 2D view',
  NO_PLAN: 'No floor plan available for this location',
  FAILED_2D: 'Failed to show tasks in 2D view',
  NO_360: 'No 360 image found near this task',
  FAILED_360: 'Failed to load 360 viewer',
});

/** Let the 3D scene settle after the model tree exists before painting it. */
const PAINT_DELAY_MS = 100;
const DISCRIMINATOR = Object.freeze({ SCAFFOLD: 'ForgeScaffold', AVA_INSPECT: 'ForgeAvaInspect' });
const SYSTEM_TIMERS = Object.freeze({ setTimeout: (fn, ms) => setTimeout(fn, ms) });

/** A representative world point of the tags (point, position, or a child's centre), or null. */
export function pointOfTags(tags) {
  for (const tag of tags) {
    if (tag.point) return tag.point;
    if (tag.position) return tag.position;
    const child = tag.children?.find((c) => c.center);
    if (child?.center) return child.center;
  }
  return null;
}

const parsedAs = (Model, tags, discriminator) => tags
  .filter((tag) => tag.discriminator === discriminator)
  .map((tag) => {
    const model = Object.assign(new Model(), tag);
    model.parseContent();
    return model;
  });

/**
 * Scaffolds and AVA Inspect tags own the camera for their kind, so they act after the shared
 * scene was painted: one AVA Inspect tag alone crops to its volume; scaffolds are isolated and
 * framed together with the other tags' points.
 */
function frameOwnKinds(viewer, tags) {
  const scaffolds = parsedAs(ForgeScaffold, tags, DISCRIMINATOR.SCAFFOLD);
  const avaInspects = parsedAs(ForgeAvaInspect, tags, DISCRIMINATOR.AVA_INSPECT);
  if (avaInspects.length === 1 && avaInspects.length === tags.length) {
    if (avaInspects[0].cropBox) viewer.cropToTag(avaInspects[0]);
    return;
  }
  if (!scaffolds.length) return;
  viewer.isolateScaffolds(scaffolds.map((s) => s.id));
  const boxPoints = scaffolds.flatMap((s) => [s.getBox().min, s.getBox().max]);
  const tagPoints = viewer.worldPointsToViewer(tags.flatMap((t) => t.point || t.position).filter(Boolean));
  viewer.zoomToBox(getBoxAroundPoints(boxPoints.concat(tagPoints).filter(Boolean)));
}

/**
 * 3D: isolation, ghosting, exact-target colour, camera fit and pulse are one decision, made by
 * ViewerTaskColoring → useSpatialFocus (`surface.paint3d`). Resolves once the scene was painted.
 */
export async function showTaskTagsIn3d({ surface, viewer, timers = SYSTEM_TIMERS }, tags) {
  if (!tags.length) return;
  await surface.load3d();
  if (!viewer.isReady()) return;
  await new Promise((resolve) => {
    viewer.whenModelLoaded(() => timers.setTimeout(async () => {
      try {
        log('%cTaskBoard: 3D isolation callback', 'color: #4caf50;');
        viewer.clearCropBox();
        await surface.paint3d();
        frameOwnKinds(viewer, tags);
      } catch (err) {
        debugError('TaskBoard: 3D isolation failed', err);
      } finally {
        resolve();
      }
    }, PAINT_DELAY_MS));
  });
}

/** 2D: the plan of the level holding the tags, zoomed to them, with the tags highlighted. */
export async function showTaskTagsIn2d({ store, surface, viewer }, tags) {
  try {
    if (!tags.length) return;
    const point = pointOfTags(tags);
    if (!point) {
      surface.notify.warning(TASK_VIEWER_TEXT.NO_POSITION);
      return;
    }
    const level = viewer.levelContaining(point) || store.state.levels?.[0];
    if (!level?.planUrn) {
      surface.notify.warning(TASK_VIEWER_TEXT.NO_PLAN);
      return;
    }
    store.commit('setSelectedLevel', level);
    await surface.load2d(level.planUrn);
    viewer.zoomToPointIn2d(point, () => viewer.highlightTagsIn2d(tags));
  } catch (err) {
    debugError('TaskBoard: 2D viewer load failed', err);
    surface.notify.error(TASK_VIEWER_TEXT.FAILED_2D);
  }
}

/**
 * The tag that leads the 360 view and its scan: the resolver's exact plan when there is one,
 * else a tag pinned to a scan (positionImage360Id, the place the user put it), else the first.
 */
function panoramaTarget(store, tags) {
  const plan = taskSpatialPlan(store, tags);
  const exact = isPanoramaPlan(plan) && !!plan.image360Id;
  const tag = exact
    ? tags.find((candidate) => !Array.isArray(plan.includedTagIds) || plan.includedTagIds.includes(candidate.id))
    : tags.find((candidate) => candidate.positionImage360Id);
  const executionTag = tag || tags[0];
  return {
    executionTag,
    image360Id: exact ? plan.image360Id : executionTag?.positionImage360Id,
    imageFacilityId: exact ? plan.facilityId : store.state.assetId,
  };
}

/** The pinned scan: from the loaded projects when there, else fetched (the board never loads them). */
async function pinnedScan(store, { image360Id, imageFacilityId }, isStale) {
  const loaded = (store.state.projects || []).flatMap((p) => p.images || []).find((img) => img.id === image360Id);
  if (loaded) return loaded;
  try {
    const { data } = await images360Service.getById(image360Id, imageFacilityId);
    if (isStale()) return null;
    return data ? Image360.fromDTO(data) : null;
  } catch (err) {
    debugError(`TaskBoard: 360 API lookup failed for image360Id=${image360Id}`, err);
    return null;
  }
}

/**
 * The scan nearest the tags. Tag points are WORLD space and the scans' camera positions VIEWER
 * space, so the point is converted first (facilities with a global offset).
 */
function nearestScan(viewer, tags) {
  const point = pointOfTags(tags);
  if (!point) return null;
  const image = viewer.closestImageTo(viewer.worldToViewer(point));
  if (image) log(`TaskBoard: 360 fell back to closest-image ${image.id}`);
  return image;
}

/** Each 360 request; an older one stops once a newer one starts ("wrong scan on quick click"). */
let panoramaRequest = 0;

/** 360: the exact (or pinned) scan, else the nearest one; a superseded request never lands. */
export async function showTaskTagsIn360({ store, surface, viewer }, tags) {
  const request = ++panoramaRequest;
  const isStale = () => request !== panoramaRequest;
  try {
    const target = panoramaTarget(store, tags);
    if (!target.executionTag) return;
    let image = null;
    if (target.image360Id) {
      image = await pinnedScan(store, target, isStale);
      if (isStale()) return;
      log(image
        ? `TaskBoard: 360 resolved exact scan ${target.image360Id}`
        : `TaskBoard: 360 exact scan ${target.image360Id} unresolved; trying closest-point`);
    }
    if (!image) image = nearestScan(viewer, tags);
    if (isStale()) return;
    if (!image) {
      surface.notify.warning(TASK_VIEWER_TEXT.NO_360);
      return;
    }
    await surface.nextTick();
    if (isStale()) return;
    await surface.load360(image, target.executionTag);
  } catch (err) {
    debugError('TaskBoard: 360 viewer load failed', err);
    surface.notify.error(TASK_VIEWER_TEXT.FAILED_360);
  }
}

const TAB_STEPS = Object.freeze({
  [TASK_VIEWER_TAB.MODEL_3D]: showTaskTagsIn3d,
  [TASK_VIEWER_TAB.PLAN_2D]: showTaskTagsIn2d,
  [TASK_VIEWER_TAB.PANORAMA]: showTaskTagsIn360,
});

/** Run the tab's own step; an unknown tab or no tags does nothing. */
export function loadTaskViewerTab(ctx, tab, tags) {
  if (!tags || !tags.length || !Object.prototype.hasOwnProperty.call(TAB_STEPS, tab)) return Promise.resolve();
  return TAB_STEPS[tab](ctx, tags);
}
